import type {SortingFn} from "@tanstack/react-table"
import type {
  DocumentStatus as BackendDocumentStatus,
  DocumentType as BackendDocumentType,
  OtkDocumentView,
  OtkOwnerType,
  Sort,
} from "shared"
import {otkDocumentCountStatusGroups} from "shared"

// documents.md's API/Data section: the entity's own real status enum
// (`dokument-otk.md` / `shared`'s `DocumentStatus`, English, 8 values:
// DRAFT/NOT_SENT/NOT_ACCEPTED/SENT/DELIVERED/VERIFIED/INVALID/ANNULLED,
// already what otk-be's real `GET /otk/documents` returns) does not match
// this page's own Ukrainian mock/UI vocabulary one-to-one. `DocumentRow`
// below stays in the Ukrainian vocabulary documents.md itself documents —
// every other page component (tabs, `Status`, `canAnnul`/`canDuplicate`,
// quick search) is built against it — and `mapOtkDocumentView` at the
// bottom of this file is the one place that translates the real backend
// response into it. `NOT_SENT`/`NOT_ACCEPTED` are failed-transport outcomes
// belonging to an integration-attempt record, not a document state, and
// project into no tab (documents.md's `fromRegistryStatus` note).
//
// Five statuses, customer decision 11.09.2026 (documents.md's Status tabs
// table) — the two registry monitoring outcomes (`ПЕРЕВІРЕНО`/`НЕДІЙСНИЙ`,
// pushed by the registry after submission) sit between `ВІДПРАВЛЕНО` and
// `АНУЛЬОВАНО`. Labels use the norm's own terms «перевірено»/«недійсний»
// (Наказ МВС № 928, п. 6), not the earlier masculine-adjectival
// `ДОСТАВЛЕНИЙ`/`ПЕРЕВІРЕНИЙ` forms.
// `НЕ ВІДПРАВЛЕНО` (01.10.2026, customer decision — `QUESTIONS-WORKLOG.md`,
// «Документ ОТК», п. 10) sits right after `ЧЕРНЕТКА`. It is backed by the
// real `SUBMISSION_PENDING` status, not by the registry's own `NOT_SENT`:
// this backend never sets `NOT_SENT` at all — `otk-document-submission.ts`
// treats "the sign request and explicit retries" as the only delivery
// triggers and leaves a failed delivery in `SUBMISSION_PENDING` with
// `submission.lastError`/`nextRetryAt`, with no automatic retry loop to
// exhaust. documents.md describes the opposite backend (auto-retry, then
// `NOT_SENT`); mapping the tab onto `SUBMISSION_PENDING` is the deliberate
// deviation that makes the feature reachable against the API we actually
// have, rather than a status nothing can produce.
export const documentStatuses = [
  "ЧЕРНЕТКА",
  "НЕ ВІДПРАВЛЕНО",
  "ВІДПРАВЛЕНО",
  "ПЕРЕВІРЕНО",
  "НЕДІЙСНИЙ",
  "АНУЛЬОВАНО",
] as const
export type DocumentStatus = (typeof documentStatuses)[number]

// Tabs, in fixed order — documents.md's Status tabs table. No "Усі" tab.
export const documentTabStatuses = documentStatuses
export type DocumentTabStatus = DocumentStatus

// Guards the router-driven `?tab=` query param (see `Documents.tsx`) —
// anything else (missing, stale, hand-typed) falls back to the default tab.
export function isDocumentTabStatus(
  value: string | null
): value is DocumentTabStatus {
  return (documentTabStatuses as readonly string[]).includes(value ?? "")
}

// Guards the router-driven `?sort=` query param (see `Documents.tsx`), same
// spirit as `isDocumentTabStatus` — anything else falls back to the default.
export function isSort(value: string | null): value is Sort {
  return value === "ASC" || value === "DESC"
}

export const documentTabLabels: Record<DocumentTabStatus, string> = {
  ЧЕРНЕТКА: "Чернетка",
  "НЕ ВІДПРАВЛЕНО": "Не відправлено",
  ВІДПРАВЛЕНО: "Відправлено",
  ПЕРЕВІРЕНО: "Перевірено",
  НЕДІЙСНИЙ: "Недійсний",
  АНУЛЬОВАНО: "Анульовано",
}

export const documentTabCountStatus = {
  ЧЕРНЕТКА: "DRAFT",
  "НЕ ВІДПРАВЛЕНО": "SUBMISSION_PENDING",
  ВІДПРАВЛЕНО: "SENT",
  ПЕРЕВІРЕНО: "VERIFIED",
  НЕДІЙСНИЙ: "INVALID",
  АНУЛЬОВАНО: "ANNULLED",
} as const satisfies Record<DocumentTabStatus, keyof typeof otkDocumentCountStatusGroups>

// Each tab's own `GET /otk/documents?status=` value(s) — `documentsListQuery`
// takes exactly one status per request, not a repeatable list, so a tab
// backed by more than one real status (`ВІДПРАВЛЕНО` covers both `SENT` and
// `DELIVERED` — documents.md: "delivery confirmation is `deliveredAt`
// metadata, not a state") fetches one request per backend status and merges
// them (see `store/documents.ts`), rather than the page spec's idealized
// single multi-status query.
export const documentTabStatusToBackend: Record<
  DocumentTabStatus,
  readonly BackendDocumentStatus[]
> = {
  ЧЕРНЕТКА: otkDocumentCountStatusGroups.DRAFT,
  "НЕ ВІДПРАВЛЕНО": otkDocumentCountStatusGroups.SUBMISSION_PENDING,
  ВІДПРАВЛЕНО: otkDocumentCountStatusGroups.SENT,
  ПЕРЕВІРЕНО: otkDocumentCountStatusGroups.VERIFIED,
  НЕДІЙСНИЙ: otkDocumentCountStatusGroups.INVALID,
  АНУЛЬОВАНО: otkDocumentCountStatusGroups.ANNULLED,
}

// documents.md's advanced-search "Типи Документа" — static, verbatim, in order.
export const documentTypes = ["ПРОТОКОЛ", "АКТ НЕВІДПОВІДНОСТІ"] as const
export type DocumentType = (typeof documentTypes)[number]

export const backendTypeToDisplay: Record<BackendDocumentType, DocumentType> = {
  PROTOCOL: "ПРОТОКОЛ",
  NONCONFORMITY_ACT: "АКТ НЕВІДПОВІДНОСТІ",
}

// Advanced-search submit's own forward direction — `documentTypeToBackend` is
// `backendTypeToDisplay` inverted, since the search form collects the
// Ukrainian display value but `otkDocumentsSearchInputSchema.types` wants the
// real backend enum.
export const documentTypeToBackend: Record<DocumentType, BackendDocumentType> = {
  ПРОТОКОЛ: "PROTOCOL",
  "АКТ НЕВІДПОВІДНОСТІ": "NONCONFORMITY_ACT",
}

// Advanced-search results (`POST /otk/documents/search`) can return any
// backend status, not just the four the search form's own
// "Статуси" checkboxes expose — leaving every status unchecked (or all
// checked) is read as "no restriction" and searches every searchable status
// server-side.
// Kept in the same all-caps Ukrainian vocabulary as `documentStatuses` so
// `statusBadgeVariant`'s `"ЧЕРНЕТКА"`/`"АНУЛ"` checks keep matching.
export const documentStatusLabels: Record<BackendDocumentStatus, string> = {
  DRAFT: "ЧЕРНЕТКА",
  NOT_SENT: "НЕ ВІДПРАВЛЕНО",
  NOT_ACCEPTED: "НЕ ПРИЙНЯТО",
  SENT: "ВІДПРАВЛЕНО",
  DELIVERED: "ДОСТАВЛЕНО",
  VERIFIED: "ПЕРЕВІРЕНО",
  // Displayed as «Не відправлено», matching the tab this status backs (see
  // `documentStatuses`) — advanced-search results carry the raw backend
  // status, so leaving the old «ОЧІКУЄ НАДСИЛАННЯ» here would label the same
  // document differently depending on which screen showed it.
  SUBMISSION_PENDING: "НЕ ВІДПРАВЛЕНО",
  INVALID: "НЕДІЙСНИЙ",
  ANNULLED: "АНУЛЬОВАНО"
}

// `OtkDocumentSearchResult.ownerType`'s display labels — same wording as
// `DocumentAdvancedSearch`'s own "Тип власника" `Select` options.
export const ownerTypeLabels: Record<OtkOwnerType, string> = {
  INDIVIDUAL: "Фізична особа",
  LEGAL_ENTITY: "Юридична особа",
}

// documents.md's API/Data response shape, one row per document — this
// client's own UI model, populated from the real `GET /otk/documents` via
// `mapOtkDocumentView`. `plate` is not one of the documented wire fields
// (the backend sends a bare `plateNumber`, no combined display string) —
// it's this client's own derived index field so quick search can match "the
// registration plate contained in `DocumentRow.vehicle`" precisely, instead
// of matching the whole display string (make/model included).
export interface DocumentRow {
  id: string
  number: string
  vehicleDisplay: string
  /** documents.md's separate «Марка/Модель» column — brand on top, model below. */
  vehicleBrand: string
  vehicleModel: string
  plate: string
  vin: string
  type: DocumentType
  status: DocumentStatus
  createdISO: string
  /**
   * The full qualified-KEP instant (`OtkDocumentView.signedAt`, untruncated)
   * — documents.md requires the full instant, not a date-only string: it
   * backs the epoch-ms sort (`signedSortingFn`), the Kyiv-calendar display
   * (`formatSignedAtDate`), and (once the backend carries it) the 24h annul
   * deadline. `null` for an unsigned draft.
   */
  signedISO: string | null
  registryAcceptedAt?: string | null
  /**
   * `OtkDocumentView.submission.nextRetryAt` — the earliest instant
   * `POST /otk/documents/:id/retry` accepts. `null` for other statuses.
   */
  nextRetryAtISO: string | null
  punktId?: string
  /** Optimistic-concurrency token for `DELETE /otk/documents/:id`. */
  version: number
}

// `AnnulDialog`'s row-action gate. documents.md: annulment is possible from
// `ВІДПРАВЛЕНО` alone — once the registry pushes a monitoring outcome
// (`ПЕРЕВІРЕНО`/`НЕДІЙСНИЙ`), `annul.ts`'s `REGISTRY_VERIFIED`/
// `REGISTRY_INVALID` become structural `hidden` reasons instead.
export function canAnnul(status: DocumentStatus): boolean {
  return status === "ВІДПРАВЛЕНО"
}

// `DuplicateDialog`'s row-action gate — any status except `ЧЕРНЕТКА` and
// `НЕ ВІДПРАВЛЕНО` (documents.md, 01.10.2026: an unsent document offers
// re-send and delete, and nothing else).
export function canDuplicate(status: DocumentStatus): boolean {
  return status !== "ЧЕРНЕТКА" && status !== "НЕ ВІДПРАВЛЕНО"
}

// `UnsentRowActions`' row-action gate. documents.md's virtual «Відправлення»
// state (02.10.2026) is deliberately **not derived from `nextRetryAt`**: the
// backend pushes `nextRetryAt` forward when an attempt *starts* and leaves it
// there when the attempt fails, so "window not elapsed" covers both a
// transfer still in flight and one that already failed — the list cannot
// tell them apart. `UnsentRowActions` therefore only shows sending for its
// own in-flight request, and otherwise disables «Повторити Відправку» until
// `nextRetryAtISO` (`isRetryWindowOpen`).
export function canRetrySend(status: DocumentStatus): boolean {
  return status === "НЕ ВІДПРАВЛЕНО"
}

// otk-be's `createdAt`/`signedAt` are full ISO timestamps, not the
// date-only strings documents.md's mock used — `createdISO` keeps the
// date-only contract every column/sort helper below already assumes, so the
// time-of-day is truncated once here rather than at every call site.
// `signedISO` deliberately does *not* go through this — see `DocumentRow`'s
// own doc comment.
export function toDateOnlyISO(value: string): string {
  return value.slice(0, 10)
}

const KYIV_TIME_ZONE = "Europe/Kyiv"

// The Kyiv calendar date derived from a full instant — documents.md
// distinguishes this explicitly from the instant itself: display and the
// `signedAfter`/`signedTo` advanced-search filters compare this value, while
// sorting and the annul deadline compare the instant's own epoch ms. Native
// `Intl.DateTimeFormat` (no `date-fns-tz` dependency needed): the `en-CA`
// locale is a reliable way to get a plain `YYYY-MM-DD` out of it.
export function signedDateKyiv(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: KYIV_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso))
}

// `dd.MM.yyyy` display for the "Підписано" column — the Kyiv calendar date,
// not a naive slice of the UTC instant (which could show the wrong day
// depending on the offset).
export function formatSignedAtDate(iso: string): string {
  const [y, m, d] = signedDateKyiv(iso).split("-")
  return `${d}.${m}.${y}`
}

// Maps one `GET /otk/documents` row (`OtkDocumentView`, the real backend
// shape) onto this client's own `DocumentRow` — the one seam between the
// English/split-field wire format and documents.md's Ukrainian/
// combined-display UI model. `tabStatus` is passed in rather than reverse-
// mapped from `view.status`, since the caller already knows it (one fetch
// per backend status making up a tab, see `store/documents.ts`) and every
// status this app ever requests belongs to exactly one of the five tabs.
export function mapOtkDocumentView(
  view: OtkDocumentView,
  tabStatus: DocumentTabStatus
): DocumentRow {
  const plate = view.plateNumber ?? ""
  const vehicleName = [view.vehicleBrand, view.vehicleModel].filter(Boolean).join(" ")
  return {
    id: view.id,
    number: view.number,
    vehicleDisplay: [vehicleName, plate].filter(Boolean).join(" · "),
    vehicleBrand: view.vehicleBrand ?? "",
    vehicleModel: view.vehicleModel ?? "",
    plate,
    vin: view.vin ?? "",
    type: backendTypeToDisplay[view.type],
    status: tabStatus,
    createdISO: toDateOnlyISO(view.createdAt),
    signedISO: view.signedAt ?? null,
    registryAcceptedAt: view.registryAcceptedAt,
    nextRetryAtISO: view.status === "SUBMISSION_PENDING"
      ? view.submission?.nextRetryAt ?? null
      : null,
    punktId: view.punktId,
    version: view.version,
  }
}

// `GET /otk/documents` sorts by `createdAt` server-side (`documentsListQuery`'s
// `sort`), but a tab backed by more than one backend status (`ВІДПРАВЛЕНО`,
// see `documentTabStatusToBackend`) is fetched as one request per status and
// merged client-side (`store/documents.ts`) — each stream arrives correctly
// ordered on its own, but interleaving two already-sorted streams still
// takes one client-side pass. Single-status tabs are already in this order
// and this is a no-op for them.
export function sortDocumentsByCreated(
  documents: DocumentRow[],
  sort: Sort
): DocumentRow[] {
  const factor = sort === "DESC" ? -1 : 1
  return [...documents].sort(
    (a, b) => factor * a.createdISO.localeCompare(b.createdISO)
  )
}

// documents.md's quick search: matches `DocumentRow.number`, the
// registration plate contained in the vehicle field, and `DocumentRow.vin`
// — case-insensitive, live.
export function documentMatchesQuickSearch(
  row: DocumentRow,
  rawQuery: string
): boolean {
  const query = rawQuery.trim()
  if (!query) return true
  const lowerQuery = query.toLocaleLowerCase()
  if (row.number.toLocaleLowerCase().includes(lowerQuery)) return true
  if (row.vin.toLocaleLowerCase().includes(lowerQuery)) return true
  return row.plate.toLocaleLowerCase().includes(lowerQuery)
}

// `signedISO`'s own `sortingFn` — documents.md: compares the full instants
// (epoch ms), not a truncated date string, so two documents signed on the
// same Kyiv calendar date still sort by time of day. `null` (unsigned
// drafts) sorts consistently to one end instead of TanStack's default
// first-row type inference.
export const signedSortingFn: SortingFn<DocumentRow> = (rowA, rowB) => {
  const a = rowA.original.signedISO
  const b = rowB.original.signedISO
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return Date.parse(a) - Date.parse(b)
}

// Whether `POST /otk/documents/:id/retry` would accept now. A row without
// `nextRetryAtISO` is not gated client-side — the backend stays the judge.
export function isRetryWindowOpen(row: DocumentRow, nowMs: number): boolean {
  return row.nextRetryAtISO === null || Date.parse(row.nextRetryAtISO) <= nowMs
}

// `HH:mm:ss` in Kyiv for the retry-window tooltip — seconds, because the
// window is only three minutes long (`SUBMISSION_RETRY_DELAY_MS`).
export function formatKyivTime(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: KYIV_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(iso))
}

// `dd.MM.yyyy - HH:mm` for the KEP timestamp itself — document-editor.md's
// own format for the "Документ Підписано" card and `SignatureDetailsDialog`'s
// «Час підпису або мітка часу:» row. Separate from `formatSignedAtDate`
// above: that one deliberately drops the time to a Kyiv calendar date for
// the list column, while a signature timestamp has to keep the clock time
// (and the dash separator) the spec asks for.
export function formatSignedAtDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: KYIV_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso))
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? ""
  return `${part("day")}.${part("month")}.${part("year")} - ${part("hour")}:${part("minute")}`
}
